<template>
    <div class="modal fade" id="appFacilitaModal" tabindex="-1" role="dialog" aria-hidden="true"
         data-backdrop="static" data-keyboard="false">
        <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Importar empreendimentos AppFacilita</h5>
                    <button id="closeAppFacilitaModal" type="button" class="close" data-dismiss="modal"
                            aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="showCredentialPicker">
                        <div v-if="credentialsLoading" class="credential-skeleton">
                            <div class="credential-card skeleton" v-for="n in 3" :key="n"></div>
                        </div>

                        <div v-else-if="!validCredentials.length" class="app-facilita-empty">
                            <div class="empty-icon"><i class="icon-lock-open"></i></div>
                            <h5>Nenhuma credencial AppFacilita válida encontrada</h5>
                            <p>Valide ou cadastre uma credencial para importar empreendimentos.</p>
                            <a class="btn btn-primary" href="/credenciais-integracoes">Cadastrar credencial</a>
                        </div>

                        <div v-else class="credential-grid">
                            <div class="credential-card"
                                 v-for="cred in validCredentials"
                                 :key="cred.id"
                                 :class="{ selected: selectedCredentialId === cred.id }"
                                 @click="$emit('select-credential', cred.id)">
                                <div class="credential-header">
                                    <div>
                                        <h6 class="mb-1">{{ cred.name }}</h6>
                                        <div class="small text-muted">AppFacilita</div>
                                    </div>
                                    <span class="badge badge-env">{{ formatEnvironmentLabel(cred.environment) }}</span>
                                </div>
                                <div class="credential-meta">
                                    <span class="badge badge-success">Ativa</span>
                                    <span class="badge badge-auth">{{ cred.auth_type }}</span>
                                </div>
                                <div class="credential-footer" v-if="cred.last_validated_at">
                                    <div class="small text-muted">Última validação</div>
                                    <div class="small">
                                        {{ formatDate(cred.last_validated_at) }}
                                        <span v-if="cred.last_validation_result?.status"
                                              :class="cred.last_validation_result?.status === 'success' ? 'text-success' : 'text-danger'">
                                            ({{ cred.last_validation_result?.status }})
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="alert alert-danger mt-3" v-if="credentialsError">
                            {{ credentialsError }}
                        </div>
                    </div>

                    <div v-else>
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <div class="small text-muted">
                                Credencial selecionada: {{ selectedCredentialName }}
                            </div>
                            <button class="btn btn-light" type="button" @click="$emit('back')"
                                    v-if="canSwitchCredential">
                                Trocar credencial
                            </button>
                        </div>

                        <div v-if="empreendimentosLoading" class="text-center p-4">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-2 mb-0">Carregando empreendimentos...</p>
                        </div>
                        <div v-else>
                            <div class="mb-3">
                                <div class="app-facilita-search">
                                    <i class="icon-search1"></i>
                                    <input
                                        type="text"
                                        class="form-control"
                                        :value="searchTerm"
                                        @input="$emit('update:searchTerm', $event.target.value)"
                                        placeholder="Buscar empreendimento por nome, código, cidade..."
                                    >
                                </div>
                            </div>
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <button class="btn btn-link p-0 mr-3" type="button"
                                            @click="$emit('toggle-all')"
                                            :disabled="!filteredEmpreendimentos.length">
                                        {{ allFilteredEmpreendimentosSelected ? 'Limpar seleção da busca' : 'Selecionar todos da busca' }}
                                    </button>
                                    <button class="btn btn-link p-0" type="button"
                                            @click="$emit('clear-selected')"
                                            :disabled="!selectedEmpreendimentos.length">
                                        Limpar seleção
                                    </button>
                                </div>
                                <div class="small text-muted app-facilita-counts">
                                    <span>Total {{ empreendimentos.length }}</span>
                                    <span>Já cadastrados {{ registeredEmpreendimentosCount }}</span>
                                    <span>Selecionados {{ selectedEmpreendimentos.length }}</span>
                                </div>
                            </div>

                            <div class="alert alert-danger mb-3" v-if="empreendimentosError">
                                {{ empreendimentosError }}
                            </div>

                            <div class="app-facilita-list p-1">
                                <div v-if="!filteredEmpreendimentos.length" class="text-muted p-2">
                                    Nenhum empreendimento encontrado.
                                </div>
                                <div class="app-facilita-item mb-2"
                                     v-for="empreendimento in filteredEmpreendimentos"
                                     :key="empreendimento.id"
                                     :class="{
                                         selected: selectedEmpreendimentos.includes(empreendimento.id),
                                         disabled: empreendimento.already_registered
                                     }"
                                     @click="$emit('toggle-empreendimento', empreendimento)">
                                    <div class="d-flex align-items-start">
                                        <div class="custom-control custom-checkbox mr-2 mt-1"
                                             v-if="!empreendimento.already_registered">
                                            <input
                                                class="custom-control-input"
                                                type="checkbox"
                                                :id="`empreendimento-${empreendimento.id}`"
                                                :checked="selectedEmpreendimentos.includes(empreendimento.id)"
                                                :disabled="empreendimento.already_registered"
                                                @click.stop
                                                @change="$emit('toggle-empreendimento', empreendimento)"
                                            >
                                            <label class="custom-control-label"
                                                   :for="`empreendimento-${empreendimento.id}`"
                                                   @click.stop></label>
                                        </div>
                                        <div v-else class="app-facilita-lock mr-2 mt-1" @click.stop>
                                            <i class="fas fa-lock"></i>
                                        </div>
                                        <div class="app-facilita-thumb mr-3">
                                            <img v-if="empreendimento.imageUrl" :src="empreendimento.imageUrl"
                                                 :alt="empreendimento.nome">
                                            <div v-else class="app-facilita-thumb-placeholder">
                                                Sem imagem
                                            </div>
                                        </div>
                                        <div class="flex-fill">
                                            <div class="d-flex justify-content-between align-items-start flex-wrap">
                                                <div>
                                                    <h6 class="mb-1 app-facilita-name">{{ empreendimento.nome }}</h6>
                                                    <div class="small text-muted">ID {{ empreendimento.id }} • Código {{
                                                            empreendimento.code || '-'
                                                        }}
                                                    </div>
                                                </div>
                                                <div class="d-flex align-items-center flex-wrap app-facilita-status-group">
                                                    <span v-if="empreendimento.already_registered"
                                                          class="app-facilita-badge">Já cadastrado</span>
                                                    <span class="app-facilita-status"
                                                          :class="empreendimento.status === 'active' ? 'active' : 'inactive'">
                                                        {{ empreendimento.status === 'active' ? 'Ativo' : (empreendimento.status || 'N/A') }}
                                                    </span>
                                                </div>
                                            </div>

                                            <p class="mb-1 mt-1 app-facilita-title"
                                               v-if="empreendimento.title">{{ empreendimento.title }}</p>
                                            <div class="small text-muted mb-2 app-facilita-location">
                                                <i class="icon-map2 mr-1"></i>{{ empreendimento.subtitle || '-' }}
                                                <span v-if="empreendimento.city || empreendimento.uf"> | {{ empreendimento.city || '-' }} - {{ empreendimento.uf || '-' }}</span>
                                            </div>

                                            <div class="app-facilita-stats">
                                                <span class="app-facilita-chip">Disponíveis <strong>{{
                                                        empreendimento.available
                                                    }}</strong></span>
                                                <span class="app-facilita-chip">Reservados <strong>{{
                                                        empreendimento.reserved
                                                    }}</strong></span>
                                                <span class="app-facilita-chip">Vendidos <strong>{{
                                                        empreendimento.sale
                                                    }}</strong></span>
                                                <span class="app-facilita-chip">Etapa <strong>{{
                                                        empreendimento.stage || '-'
                                                    }}</strong></span>
                                                <span class="app-facilita-chip">Quadras <strong>{{
                                                        empreendimento.blocks || '-'
                                                    }}</strong></span>
                                                <span class="app-facilita-chip">Unidades <strong>{{
                                                        empreendimento.quantity || '-'
                                                    }}</strong></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-light" data-dismiss="modal">Cancelar</button>
                    <button
                        type="button"
                        class="btn btn-custom bg-btn"
                        @click="$emit('import-selected')"
                        :disabled="showCredentialPicker || !selectedEmpreendimentos.length || importandoEmpreendimentos"
                    >
                        {{ importandoEmpreendimentos ? 'Importando...' : 'Importar selecionados' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AppFacilitaModal',
    props: {
        showCredentialPicker: {type: Boolean, default: true},
        credentialsLoading: {type: Boolean, default: false},
        validCredentials: {type: Array, default: () => []},
        selectedCredentialId: {type: [String, Number], default: null},
        credentialsError: {type: String, default: ''},
        selectedCredentialName: {type: String, default: ''},
        canSwitchCredential: {type: Boolean, default: false},
        empreendimentosLoading: {type: Boolean, default: false},
        searchTerm: {type: String, default: ''},
        filteredEmpreendimentos: {type: Array, default: () => []},
        allFilteredEmpreendimentosSelected: {type: Boolean, default: false},
        selectedEmpreendimentos: {type: Array, default: () => []},
        empreendimentos: {type: Array, default: () => []},
        registeredEmpreendimentosCount: {type: Number, default: 0},
        empreendimentosError: {type: String, default: ''},
        importandoEmpreendimentos: {type: Boolean, default: false},
        formatEnvironmentLabel: {type: Function, required: true},
        formatDate: {type: Function, required: true},
    },
};
</script>

<style scoped>
.app-facilita-list {
    max-height: 60vh;
    overflow-y: auto;
}

.app-facilita-search {
    position: relative;
}

.app-facilita-search i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #7f8c9a;
}

.app-facilita-search input {
    padding-left: 34px;
    border-radius: 10px;
    border: 1px solid #dfe4ea;
}

.app-facilita-dropdown-item {
    display: flex;
    justify-content: center;
    align-items: center;
}

.app-facilita-item {
    border: 1px solid #e7ebf0;
    border-radius: 12px;
    padding: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
    transition: all 0.2s ease;
}

.app-facilita-item:hover {
    transform: translateY(-1px);
    border-color: #cfd8e3;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.app-facilita-item.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.app-facilita-item.disabled:hover {
    transform: none;
    border-color: #e7ebf0;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.app-facilita-item.selected {
    border-color: #0e5e4e;
    background: linear-gradient(180deg, #f4faf8 0%, #eff7f5 100%);
    box-shadow: 0 8px 16px rgba(14, 94, 78, 0.12);
}

.app-facilita-thumb {
    width: 110px;
    min-width: 110px;
    height: 82px;
    border-radius: 10px;
    overflow: hidden;
    background: #f2f2f2;
    border: 1px solid #e6ebf1;
}

.app-facilita-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.app-facilita-thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #adb5bd;
    font-size: 20px;
}

.app-facilita-title {
    font-weight: 500;
    color: #3a424c;
    font-size: 13px;
    line-height: 1.35;
}

.app-facilita-stats {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.app-facilita-name {
    color: #1d2a3a;
    font-weight: 700;
    letter-spacing: .1px;
}

.app-facilita-location {
    display: flex;
    align-items: center;
}

.app-facilita-chip {
    background: #f3f6fa;
    border: 1px solid #e2e8f0;
    color: #415166;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 11px;
}

.app-facilita-chip strong {
    color: #223248;
    margin-left: 4px;
}

.app-facilita-status {
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .3px;
    text-transform: uppercase;
}

.app-facilita-status-group {
    gap: 6px;
}

.app-facilita-status.active {
    color: #0b664f;
    background: #daf5ee;
}

.app-facilita-status.inactive {
    color: #667085;
    background: #eef2f6;
}

.app-facilita-badge {
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 700;
    background: #fef3c7;
    color: #92400e;
    text-transform: uppercase;
    letter-spacing: .3px;
}

.app-facilita-counts {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.app-facilita-lock {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #9aa4b2;
    font-size: 14px;
}

.credential-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
}

.credential-card {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 14px;
    background: #fff;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
    transition: all 0.2s ease;
    cursor: pointer;
    position: relative;
    overflow: hidden;
}

.credential-card:hover {
    transform: translateY(-2px);
    border-color: #cfd8e3;
    box-shadow: 0 10px 18px rgba(15, 23, 42, 0.1);
}

.credential-card.selected {
    border-color: #0e5e4e;
    box-shadow: 0 10px 20px rgba(14, 94, 78, 0.15);
}

.credential-card.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.credential-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.credential-meta {
    display: flex;
    gap: 8px;
    margin-top: 10px;
    flex-wrap: wrap;
}

.credential-footer {
    margin-top: 12px;
}

.credential-overlay {
    position: absolute;
    top: 12px;
    right: -30px;
    background: #eef2f6;
    color: #667085;
    padding: 4px 40px;
    transform: rotate(35deg);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .3px;
}

.badge-env {
    background: #e0f2fe;
    color: #075985;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 600;
}

.badge-auth {
    background: #ede9fe;
    color: #5b21b6;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 600;
}

.app-facilita-empty {
    text-align: center;
    padding: 24px;
    border-radius: 16px;
    border: 1px dashed #d1d5db;
    background: #fafafa;
}

.app-facilita-empty .empty-icon {
    font-size: 30px;
    color: #9aa4b2;
    margin-bottom: 8px;
}

.credential-skeleton {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
}

.credential-card.skeleton {
    height: 120px;
    background: linear-gradient(90deg, #f0f2f7, #e5e7ef, #f0f2f7);
    background-size: 200% 100%;
    animation: shimmer 1.6s infinite;
    border: none;
    box-shadow: none;
}

@keyframes shimmer {
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
}
</style>
